import type { CareerSave, SeasonEvent, TourGolfer } from './types';

export interface TournamentField {
  golfers: { id: string; name: string; shortName?: string; ranking: number; isPlayer: boolean }[];
  playerId: string;
  pairedOpponentId: string;
  rankingStrength: number;
  seed: number;
}
const seeded = (seed: number) => {
  let state = seed >>> 0;
  return () => (state = (Math.imul(state, 1664525) + 1013904223) >>> 0) / 4294967296;
};

/** Pure, deterministic field construction. No state is regenerated when a save resumes. */
export class TournamentFieldBuilder {
  build(
    event: SeasonEvent,
    career: CareerSave,
    golfers: readonly TourGolfer[],
    seed: number,
  ): TournamentField {
    const playerId = career.playerProfile.id;
    const size = Math.max(2, event.tier === 'seasonChampionship' ? 30 : event.fieldSize);
    let pool = [...golfers];
    if (event.tier === 'major') pool.sort((a, b) => a.currentRanking - b.currentRanking);
    else pool.sort((a, b) => a.currentRanking - b.currentRanking || a.id.localeCompare(b.id));
    if (event.tier !== 'major' && event.tier !== 'seasonChampionship') {
      const random = seeded(seed);
      pool = pool
        .map((golfer) => ({ golfer, key: random() }))
        .sort((a, b) => a.key - b.key)
        .map(({ golfer }) => golfer);
    }
    const ai = pool.slice(0, Math.min(size - 1, pool.length));
    const playerRanking = career.playerProfile.careerRanking;
    const insertion = Math.min(
      ai.length,
      Math.max(
        0,
        ai.findIndex((g) => g.currentRanking >= playerRanking),
      ),
    );
    const field = ai.map((g) => ({
      id: g.id,
      name: g.name,
      shortName: g.shortName,
      ranking: g.currentRanking,
      isPlayer: false,
    }));
    field.splice(insertion < 0 ? field.length : insertion, 0, {
      id: playerId,
      name: career.playerProfile.name,
      shortName: undefined,
      ranking: playerRanking,
      isPlayer: true,
    });
    const playerIndex = field.findIndex((g) => g.id === playerId);
    const neighbor = field[playerIndex % 2 === 0 ? playerIndex + 1 : playerIndex - 1] ?? ai[0];
    return {
      golfers: field,
      playerId,
      pairedOpponentId: neighbor.id,
      rankingStrength: event.rankingStrength,
      seed,
    };
  }
}
